/**
 * Cache de assets no IndexedDB (blob storage + LRU eviction).
 *
 * Fluxo:
 *   1. ensureReady(assetId, url) → verifica se o blob está em cache
 *   2. Se não → baixa e persiste no IndexedDB
 *   3. Cria/reutiliza um blob: URL em memória para renderização
 *   4. LRU eviction quando acima de 80% da quota
 */

import { sha256 } from '@/utils/checksum'
import { playerLogger } from '@/utils/player-logger'
import { getDB } from './db'

const CACHE_QUOTA_BYTES = 500 * 1024 * 1024 // 500 MB para browser
const EVICTION_THRESHOLD = 0.8 // evict quando > 80% cheio
const MAX_CONCURRENT_DOWNLOADS = 3
/**
 * Timeouts do download (F-26, revisão 2026-10-26): antes o timer era limpo ao chegarem os headers e
 * `response.blob()` podia ficar pendurado para sempre, prendendo um dos 3 slots. Agora o
 * AbortController vive até o fim do corpo, com dois limites:
 *  - STALL: nenhum byte novo nesse intervalo → aborta (conexão morta);
 *  - TOTAL: teto absoluto do download inteiro (headers + corpo), mesmo com bytes pingando.
 */
export const DOWNLOAD_STALL_TIMEOUT_MS = 60_000
export const DOWNLOAD_TOTAL_TIMEOUT_MS = 10 * 60_000
/**
 * Tamanho máximo que o player baixa para o cache (F-26). O blob inteiro + o ArrayBuffer do SHA-256
 * ficam em memória ao mesmo tempo (~2x o arquivo); acima disso o kiosk corre risco de OOM. Assets
 * maiores continuam tocando via stream (HLS/direto), só não são cacheados nem verificados.
 */
export const MAX_CACHEABLE_ASSET_BYTES = 150 * 1024 * 1024
/** Após uma verificação em background falhar por erro transitório, não tentar de novo antes disso —
 *  sem isso cada play em stream disparava um novo download completo do MP4 (F-26). */
export const VERIFY_RETRY_BACKOFF_MS = 10 * 60_000

// blob: URLs em memória (recriados a cada sessão)
const blobUrls = new Map<string, string>()
/** Checksum do conteúdo por trás de cada blob: URL em memória — permite invalidar o blob quando a
 *  agenda passa a pedir outro checksum (asset re-enviado) sem esperar reiniciar o player (L-14). */
const blobChecksums = new Map<string, string>()

/**
 * Conteúdo cujo download completo NÃO bateu com o checksum da agenda: assetId → checksum esperado.
 * Enquanto a agenda continuar pedindo o mesmo checksum, o renderer não reproduz esse asset nem via
 * stream (LOW-8). Um checksum novo na agenda (asset re-enviado) libera nova tentativa.
 */
const rejectedChecksums = new Map<string, string>()

/** Verificações em background em andamento (dedupe por assetId). */
const pendingVerifications = new Set<string>()
/** assetId → { checksum, retryAt }: verificação falhou por erro transitório — aguarda o backoff. */
const failedVerifications = new Map<string, { checksum: string; retryAt: number }>()
/** assetId → checksum pedido: asset acima de MAX_CACHEABLE_ASSET_BYTES — não baixar de novo enquanto
 *  a agenda pedir o mesmo conteúdo. */
const oversizedAssets = new Map<string, string | undefined>()
/** Fila serial: uma verificação de cada vez, para não competir com a reprodução no kiosk. */
let verificationChain: Promise<void> = Promise.resolve()

export class AssetChecksumMismatchError extends Error {
  constructor(assetId: string, expected: string, actual: string) {
    super(`Checksum mismatch for asset ${assetId}: expected ${expected}, got ${actual}`)
    this.name = 'AssetChecksumMismatchError'
  }
}

export class AssetTooLargeError extends Error {
  constructor(assetId: string, sizeBytes: number) {
    super(`Asset ${assetId} exceeds the cacheable size limit (${sizeBytes} bytes)`)
    this.name = 'AssetTooLargeError'
  }
}

export class AssetDownloadTimeoutError extends Error {
  constructor(assetId: string, reason: 'stall' | 'total') {
    super(`Download of asset ${assetId} timed out (${reason})`)
    this.name = 'AssetDownloadTimeoutError'
  }
}

// Semáforo para limitar downloads paralelos
let activeDownloads = 0
const downloadQueue: Array<() => void> = []

function releaseSlot(): void {
  activeDownloads--
  if (downloadQueue.length > 0) {
    const next = downloadQueue.shift()
    if (next) {
      activeDownloads++
      next()
    }
  }
}

async function acquireSlot(): Promise<void> {
  if (activeDownloads < MAX_CONCURRENT_DOWNLOADS) {
    activeDownloads++
    return
  }
  return new Promise((resolve) => {
    downloadQueue.push(resolve)
  })
}

/** Rejeita assim que o signal abortar — cobre corpos lidos via `blob()` (sem stream), que não
 *  reagem ao abort em todos os ambientes. */
function untilAborted<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(signal.reason)
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(signal.reason)
    signal.addEventListener('abort', onAbort, { once: true })
    promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort)
        resolve(value)
      },
      (err: unknown) => {
        signal.removeEventListener('abort', onAbort)
        reject(err)
      }
    )
  })
}

/**
 * Baixa o asset inteiro com timeout cobrindo headers E corpo (F-26) e limite de tamanho. O corpo é
 * lido em stream quando disponível, para medir progresso (stall) e abortar ao passar do limite sem
 * materializar o arquivo inteiro.
 */
async function downloadAsset(assetId: string, url: string): Promise<Blob> {
  const controller = new AbortController()
  let timeoutReason: 'stall' | 'total' | null = null
  const abortWith = (reason: 'stall' | 'total') => {
    timeoutReason = reason
    controller.abort(new AssetDownloadTimeoutError(assetId, reason))
  }
  const totalTimer = setTimeout(() => abortWith('total'), DOWNLOAD_TOTAL_TIMEOUT_MS)
  let stallTimer = setTimeout(() => abortWith('stall'), DOWNLOAD_STALL_TIMEOUT_MS)
  const touch = () => {
    clearTimeout(stallTimer)
    stallTimer = setTimeout(() => abortWith('stall'), DOWNLOAD_STALL_TIMEOUT_MS)
  }

  try {
    const response = await untilAborted(fetch(url, { signal: controller.signal }), controller.signal)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    touch()

    const declared = Number(response.headers?.get('content-length') ?? Number.NaN)
    if (Number.isFinite(declared) && declared > MAX_CACHEABLE_ASSET_BYTES) {
      controller.abort()
      throw new AssetTooLargeError(assetId, declared)
    }

    const reader = response.body?.getReader()
    if (!reader) {
      const blob = await untilAborted(response.blob(), controller.signal)
      if (blob.size > MAX_CACHEABLE_ASSET_BYTES) throw new AssetTooLargeError(assetId, blob.size)
      return blob
    }

    const chunks: Uint8Array[] = []
    let received = 0
    for (;;) {
      const { done, value } = await untilAborted(reader.read(), controller.signal)
      if (done) break
      received += value.byteLength
      if (received > MAX_CACHEABLE_ASSET_BYTES) {
        await reader.cancel().catch(() => undefined)
        throw new AssetTooLargeError(assetId, received)
      }
      chunks.push(value)
      touch()
    }
    return new Blob(chunks as BlobPart[], { type: response.headers?.get('content-type') ?? '' })
  } catch (err) {
    if (timeoutReason) throw new AssetDownloadTimeoutError(assetId, timeoutReason)
    throw err
  } finally {
    clearTimeout(totalTimer)
    clearTimeout(stallTimer)
  }
}

function forgetBlob(assetId: string): void {
  const url = blobUrls.get(assetId)
  if (url) URL.revokeObjectURL(url)
  blobUrls.delete(assetId)
  blobChecksums.delete(assetId)
}

/** O blob em memória ainda corresponde ao conteúdo que a agenda pede? Sem checksum esperado,
 *  qualquer blob serve (comportamento anterior). */
function memoryBlobMatches(assetId: string, expectedChecksum?: string): boolean {
  if (!blobUrls.has(assetId)) return false
  if (!expectedChecksum) return true
  return blobChecksums.get(assetId) === expectedChecksum
}

function rememberBlob(assetId: string, blob: Blob, checksum: string): string {
  forgetBlob(assetId)
  const blobUrl = URL.createObjectURL(blob)
  blobUrls.set(assetId, blobUrl)
  blobChecksums.set(assetId, checksum)
  return blobUrl
}

export const assetCache = {
  /**
   * Retorna o blob: URL em memória se já existir (e, com `expectedChecksum`, se o conteúdo ainda for
   * o pedido pela agenda), sem tocar o IndexedDB. Útil para decisões síncronas no renderer.
   */
  peekBlobUrl(assetId: string, expectedChecksum?: string): string | undefined {
    return memoryBlobMatches(assetId, expectedChecksum) ? blobUrls.get(assetId) : undefined
  },

  /**
   * Verifica se o asset está disponível localmente (memória ou IndexedDB) com o checksum esperado.
   * Não dispara download. Usado pelo renderer para decidir entre HLS e blob.
   */
  async isReady(assetId: string, expectedChecksum?: string): Promise<boolean> {
    if (memoryBlobMatches(assetId, expectedChecksum)) return true
    try {
      const db = await getDB()
      const cached = await db.get('assets', assetId)
      return !!cached && (!expectedChecksum || cached.checksum === expectedChecksum)
    } catch {
      return false
    }
  },

  /**
   * Garante que o asset está disponível localmente.
   * Retorna um blob: URL para uso no renderer.
   *
   * `expectedChecksum` (SHA-256 do asset original, vindo da agenda compilada) é usado para:
   *  - invalidar um cache local (memória ou IndexedDB) cujo conteúdo mudou no servidor;
   *  - verificar a integridade de um download novo antes de reproduzi-lo (hash real, uma vez
   *    por asset — nunca recalculado a cada play, para não travar o navegador do kiosk).
   */
  async ensureReady(assetId: string, url: string, expectedChecksum?: string): Promise<string> {
    // 1. Verificar blob: URL em memória — descartado se a agenda mudou o checksum (L-14)
    const existingBlobUrl = blobUrls.get(assetId)
    if (existingBlobUrl && memoryBlobMatches(assetId, expectedChecksum)) return existingBlobUrl
    if (existingBlobUrl) forgetBlob(assetId)

    // 2. Verificar IndexedDB — só reutiliza se o checksum ainda bate com o da agenda atual
    try {
      const db = await getDB()
      const cached = await db.get('assets', assetId)
      if (cached && (!expectedChecksum || cached.checksum === expectedChecksum)) {
        const blobUrl = rememberBlob(assetId, cached.blob, cached.checksum)
        // Atualizar cachedAt para LRU
        await db.put('assets', { ...cached, cachedAt: Date.now() })
        return blobUrl
      }
    } catch (err) {
      playerLogger.warn('[asset-cache] Erro ao ler IndexedDB:', err)
    }

    // 3. Baixar asset
    await acquireSlot()
    try {
      let blob: Blob
      try {
        blob = await downloadAsset(assetId, url)
      } catch (err) {
        if (err instanceof AssetTooLargeError) oversizedAssets.set(assetId, expectedChecksum)
        throw err
      }
      const checksum = await sha256(await blob.arrayBuffer())

      if (expectedChecksum && checksum !== expectedChecksum) {
        rejectedChecksums.set(assetId, expectedChecksum)
        throw new AssetChecksumMismatchError(assetId, expectedChecksum, checksum)
      }

      // 4. Persistir no IndexedDB
      try {
        const db = await getDB()
        await assetCache.evictIfNeeded(db, blob.size)
        await db.put('assets', {
          assetId,
          blob,
          originalUrl: url,
          checksum,
          cachedAt: Date.now(),
          sizeBytes: blob.size
        })
      } catch (err) {
        playerLogger.warn('[asset-cache] Erro ao persistir no IndexedDB:', err)
      }

      return rememberBlob(assetId, blob, checksum)
    } finally {
      releaseSlot()
    }
  },

  /**
   * true quando um download completo anterior deste asset falhou na validação do checksum que a
   * agenda ainda pede — o conteúdo servido é comprovadamente diferente do aprovado.
   */
  isRejected(assetId: string, expectedChecksum?: string): boolean {
    if (!expectedChecksum) return false
    return rejectedChecksums.get(assetId) === expectedChecksum
  },

  /**
   * Baixa e valida o asset em background (fire-and-forget), para que os próximos plays saiam do
   * cache verificado em vez do stream não verificado (LOW-8). Serializado (um por vez) e deduplicado
   * por assetId; erros só são logados. Sem checksum esperado não há o que verificar → no-op.
   * Não repete (F-26): asset acima de MAX_CACHEABLE_ASSET_BYTES nunca é re-baixado para o mesmo
   * checksum, e uma falha transitória espera VERIFY_RETRY_BACKOFF_MS antes da próxima tentativa.
   * Retorna a promise da verificação (útil para testes); o chamador normalmente a ignora.
   */
  verifyInBackground(assetId: string, url: string, expectedChecksum?: string): Promise<void> {
    if (!expectedChecksum || memoryBlobMatches(assetId, expectedChecksum) || pendingVerifications.has(assetId)) {
      return Promise.resolve()
    }
    if (rejectedChecksums.get(assetId) === expectedChecksum) return Promise.resolve()
    if (oversizedAssets.has(assetId) && oversizedAssets.get(assetId) === expectedChecksum) {
      return Promise.resolve()
    }
    const failure = failedVerifications.get(assetId)
    if (failure && failure.checksum === expectedChecksum && Date.now() < failure.retryAt) {
      return Promise.resolve()
    }

    pendingVerifications.add(assetId)
    const run = verificationChain.then(async () => {
      try {
        // Reaproveita o download do cache (ensureReady): o conteúdo verificado aqui é o mesmo que os
        // próximos plays reproduzem — nunca há um segundo download só para verificar.
        await assetCache.ensureReady(assetId, url, expectedChecksum)
        failedVerifications.delete(assetId)
      } catch (err) {
        if (!(err instanceof AssetChecksumMismatchError) && !(err instanceof AssetTooLargeError)) {
          failedVerifications.set(assetId, {
            checksum: expectedChecksum,
            retryAt: Date.now() + VERIFY_RETRY_BACKOFF_MS
          })
        }
        playerLogger.warn('[asset-cache] Verificação em background falhou:', { assetId, err })
      } finally {
        pendingVerifications.delete(assetId)
      }
    })
    verificationChain = run
    return run
  },

  /**
   * Remove assets menos recentemente usados para liberar espaço.
   */
  async evictIfNeeded(db: Awaited<ReturnType<typeof import('./db').getDB>>, incomingBytes: number): Promise<void> {
    try {
      const estimate = await navigator.storage.estimate()
      const used = estimate.usage ?? 0
      const quota = estimate.quota ?? CACHE_QUOTA_BYTES

      if (used + incomingBytes < quota * EVICTION_THRESHOLD) return

      // Busca todos os assets ordenados por cachedAt ASC (LRU)
      const all = await db.getAllFromIndex('assets', 'by-cachedAt')
      let freedBytes = 0
      const needed = incomingBytes + used - quota * EVICTION_THRESHOLD

      for (const asset of all) {
        if (freedBytes >= needed) break
        forgetBlob(asset.assetId)
        await db.delete('assets', asset.assetId)
        freedBytes += asset.sizeBytes
      }
    } catch (err) {
      playerLogger.warn('[asset-cache] Eviction falhou:', err)
    }
  },

  /**
   * Reconstrói o cache persistente (IndexedDB) — comando remoto `clear_cache`, para cache corrompido ou cheio.
   *
   * - Apaga tudo e regrava só os blobs em memória: eles já passaram pela verificação de checksum, e sem regravar
   *   o player ficaria sem mídia offline até o próximo reload (o blob em memória tem prioridade em ensureReady e
   *   nada voltaria ao IndexedDB). Revogar as URLs cortaria o vídeo em curso, então elas ficam.
   * - Zera as marcas de tamanho e de backoff: download falhando volta a tentar já. A marca de checksum RECUSADO
   *   fica: limpá-la liberaria o stream sem verificação de um conteúdo comprovadamente diferente do aprovado. Um
   *   checksum novo na agenda (conteúdo corrigido) já libera o asset.
   */
  async clearPersistent(): Promise<void> {
    const db = await getDB()
    const origins = new Map((await db.getAll('assets')).map((asset) => [asset.assetId, asset.originalUrl]))
    await db.clear('assets')
    failedVerifications.clear()
    oversizedAssets.clear()

    for (const [assetId, blobUrl] of blobUrls) {
      const checksum = blobChecksums.get(assetId)
      if (!checksum) continue
      try {
        const blob = await (await fetch(blobUrl)).blob()
        await db.put('assets', {
          assetId,
          blob,
          originalUrl: origins.get(assetId) ?? '',
          checksum,
          cachedAt: Date.now(),
          sizeBytes: blob.size
        })
      } catch (err) {
        playerLogger.warn('[asset-cache] Erro ao regravar blob em memória no cache limpo:', err)
      }
    }
  },

  /**
   * Retorna o tamanho total ocupado pelo cache (em bytes).
   */
  async getCacheSize(): Promise<number> {
    try {
      const db = await getDB()
      let total = 0
      let cursor = await db.transaction('assets').store.openCursor()
      while (cursor) {
        total += cursor.value.sizeBytes
        cursor = await cursor.continue()
      }
      return total
    } catch {
      return 0
    }
  }
}
